'use client';

import { useCallback, useState } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { CredentialInboxMessageDto } from '@terra/shared-contracts-applications';
import { MfaProviderSchema } from '@terra/shared-schemas-orchestrator';
import { MetaDefault } from '../../components/typography';
import { MfaMessagesToggleHeader } from '../mfa/MfaMessagesToggleHeader';
import { EmailMessageBody } from '../mfa/EmailMessageBody';
import { EmailRawSourceViewer } from '../mfa/EmailRawSourceViewer';

export type FetchCredentialInboxFn = (params: {
  credentialId: string;
}) => Promise<{
  success: boolean;
  messages?: CredentialInboxMessageDto[];
  emailAddress?: string;
  error?: string;
}>;

type CredentialMailboxSource = {
  generatedMailboxAddress?: string | null;
  mfaProviders?: Array<{
    providerType: string;
    enabled?: boolean;
    emailAddress?: string | null;
  }> | null;
};

export const resolveGeneratedMailboxAddress = ({
  credential,
}: {
  credential: CredentialMailboxSource;
}): string => {
  const emailMfaAddress =
    credential.mfaProviders?.find(
      (provider) =>
        provider.providerType === MfaProviderSchema.enum.EMAIL &&
        provider.enabled !== false &&
        Boolean(provider.emailAddress),
    )?.emailAddress ?? '';

  return emailMfaAddress || credential.generatedMailboxAddress || '';
};

export const credentialHasResolvableMailbox = ({
  credential,
}: {
  credential: CredentialMailboxSource;
}): boolean => Boolean(resolveGeneratedMailboxAddress({ credential }));

type CredentialInboxMessagesPanelProps = {
  credentialId: string;
  onFetchCredentialInbox: FetchCredentialInboxFn;
  disabled?: boolean;
};

export function CredentialInboxMessagesPanel({
  credentialId,
  onFetchCredentialInbox,
  disabled = false,
}: CredentialInboxMessagesPanelProps) {
  const t = useTranslations('designSystem.mfa.emailDisplay');
  const [isMessagesExpanded, setIsMessagesExpanded] = useState(false);
  const [messages, setMessages] = useState<CredentialInboxMessageDto[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const loadMessages = useCallback(async () => {
    setIsLoadingMessages(true);

    try {
      const result = await onFetchCredentialInbox({ credentialId });

      if (result.success && result.messages) {
        setMessages(result.messages);
        setLoadFailed(false);
      } else {
        setLoadFailed(true);
      }
    } catch (error) {
      console.error('Failed to fetch credential inbox messages:', error);
      setLoadFailed(true);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [credentialId, onFetchCredentialInbox]);

  const handleToggleExpanded = useCallback(async () => {
    if (isMessagesExpanded) {
      setIsMessagesExpanded(false);

      return;
    }

    setIsMessagesExpanded(true);
    await loadMessages();
  }, [isMessagesExpanded, loadMessages]);

  return (
    <VStack alignItems="stretch" gap={3} pt={1}>
      <MfaMessagesToggleHeader
        title={t('latestEmailMessages')}
        isExpanded={isMessagesExpanded}
        onToggle={handleToggleExpanded}
        toggleDisabled={disabled}
        toggleLoading={isLoadingMessages && !isMessagesExpanded}
        toggleAriaLabel={
          isMessagesExpanded ? t('hideMessages') : t('showMessages')
        }
        buttonSize="sm"
        iconSize={16}
        refreshLabel={t('refresh')}
        onRefresh={loadMessages}
        refreshLoading={isLoadingMessages}
        refreshDisabled={disabled}
        refreshButtonSize="sm"
      />

      {isMessagesExpanded && (
        <Box
          p={4}
          borderRadius="lg"
          bg="bg.muted"
          borderWidth="1px"
          borderColor="border.muted"
          minHeight="120px"
        >
          {isLoadingMessages && messages.length === 0 ? (
            <VStack justifyContent="center" alignItems="center" height="100px">
              <MetaDefault color="fg.muted">{t('loadingMessages')}</MetaDefault>
            </VStack>
          ) : (
            <VStack alignItems="stretch" gap={3}>
              {loadFailed ? (
                <MetaDefault color="fg.error" textAlign="center">
                  {t('failedToLoadMessages')}
                </MetaDefault>
              ) : null}
              {messages.length === 0 && !loadFailed ? (
                <VStack
                  justifyContent="center"
                  alignItems="center"
                  height="100px"
                >
                  <MetaDefault color="fg.muted" textAlign="center">
                    {t('noEmailMessages')}
                  </MetaDefault>
                </VStack>
              ) : null}
              {messages.slice(0, 3).map((message) => (
                <Box
                  key={message.id}
                  p={3}
                  bg="bg.default"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="border.muted"
                >
                  <VStack alignItems="stretch" gap={2}>
                    <VStack alignItems="flex-start" gap={0.5}>
                      <MetaDefault color="fg.muted">
                        {new Date(message.receivedAt).toLocaleString()}
                      </MetaDefault>
                      {message.from && (
                        <MetaDefault color="fg.muted" fontFamily="mono">
                          {t('from', { sender: message.from })}
                        </MetaDefault>
                      )}
                    </VStack>
                    <EmailMessageBody
                      content={message.content}
                      links={message.links}
                    />
                    <EmailRawSourceViewer
                      rawPlain={message.rawPlain}
                      rawHtml={message.rawHtml}
                    />
                  </VStack>
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      )}
    </VStack>
  );
}
